import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Emails that are always granted admin role on login or page load
const ADMIN_EMAILS = new Set(['avels@uni.coventry.ac.uk']);

export interface StoredAccount {
  id: string;
  name: string;
  email: string;
  passwordHash: string;
  role: 'parent' | 'admin';
  createdAt: string;
}

interface AuthState {
  accounts: StoredAccount[];
  currentAccountId: string | null;
  register: (name: string, email: string, password: string) => { success: boolean; error?: string };
  login: (email: string, password: string) => { success: boolean; error?: string };
  logout: () => void;
  syncAdminRoles: () => void;
}

function hashPassword(password: string): string {
  let hash = 5381;
  for (let i = 0; i < password.length; i++) {
    hash = ((hash << 5) + hash + password.charCodeAt(i)) & 0xffffffff;
  }
  return (hash >>> 0).toString(16);
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accounts: [],
      currentAccountId: null,

      register: (name, email, password) => {
        const { accounts } = get();
        const normalised = email.toLowerCase().trim();
        if (accounts.some((a) => a.email === normalised)) {
          return { success: false, error: 'An account with this email already exists.' };
        }
        const account: StoredAccount = {
          id: 'acc-' + Date.now(),
          name: name.trim(),
          email: normalised,
          passwordHash: hashPassword(password),
          role: 'parent',
          createdAt: new Date().toISOString(),
        };
        set((s) => ({ accounts: [...s.accounts, account], currentAccountId: account.id }));
        return { success: true };
      },

      login: (email, password) => {
        const { accounts } = get();
        const normalised = email.toLowerCase().trim();
        const account = accounts.find((a) => a.email === normalised);
        if (!account) return { success: false, error: 'No account found with that email.' };
        if (account.passwordHash !== hashPassword(password)) {
          return { success: false, error: 'Incorrect password.' };
        }
        set({ currentAccountId: account.id });
        return { success: true };
      },

      logout: () => set({ currentAccountId: null }),

      syncAdminRoles: () => {
        set((s) => ({
          accounts: s.accounts.map((a) =>
            ADMIN_EMAILS.has(a.email) && a.role !== 'admin'
              ? { ...a, role: 'admin' }
              : a
          ),
        }));
      },
    }),
    { name: 'lifeskills-auth-store' }
  )
);
